import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth.store';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ContractStatusBanner } from '../../components/banking/ContractStatusBanner';
import { KycStatusCard } from '../../components/banking/KycStatusCard';
import {
  Send,
  QrCode,
  FileText,
  Gift,
  Briefcase,
  Calendar,
  Clock,
  Eye,
  EyeOff,
  ArrowDownLeft,
  ArrowUpRight,
  Bell,
  DollarSign,
  TrendingUp,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function EmployeeDashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [showBalance, setShowBalance] = useState(true);

  // Fetch balance
  const { data: balanceData, isLoading: balanceLoading } = useQuery({
    queryKey: ['employee', 'balance'],
    queryFn: async () => {
      const response = await api.get('/banking/balance');
      return response.data;
    },
  });

  // Fetch salary advance availability
  const { data: advanceData, isLoading: advanceLoading } = useQuery({
    queryKey: ['employee', 'salary-advance', 'availability'],
    queryFn: async () => {
      const response = await api.get('/banking/employee/salary-advance/availability');
      return response.data;
    },
  });

  // Fetch next payment info
  const { data: paymentInfo } = useQuery({
    queryKey: ['employee', 'next-payment'],
    queryFn: async () => {
      const response = await api.get('/banking/employee/payslip/next');
      return response.data;
    },
  });

  // Fetch benefits summary
  const { data: benefitsData } = useQuery({
    queryKey: ['employee', 'benefits', 'summary'],
    queryFn: async () => {
      const response = await api.get('/banking/employee/benefits/summary');
      return response.data;
    },
  });

  // Fetch recent transactions
  const { data: transactionsData, isLoading: transactionsLoading } = useQuery({
    queryKey: ['employee', 'transactions', 'recent'],
    queryFn: async () => {
      const response = await api.get('/banking/statement', { params: { take: 5 } });
      return response.data;
    },
  });

  // Fetch HR notifications
  const { data: hrNotifications } = useQuery({
    queryKey: ['employee', 'hr-notifications'],
    queryFn: async () => {
      const response = await api.get('/banking/employee/hr-requests/notifications');
      return response.data;
    },
  });

  // Fetch contract status
  const { data: contractData } = useQuery({
    queryKey: ['banking', 'contract-status'],
    queryFn: async () => {
      try {
        const response = await api.get('/banking/contract-status');
        return response.data;
      } catch {
        return {
          contractStatus: 'ACTIVE',
          kycOriginStatus: 'APPROVED',
          kycDestinationStatus: 'APPROVED',
        };
      }
    },
  });


  const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseInt(value, 10) || 0 : value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(num / 100);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  const getDaysUntilPayment = () => {
    if (!paymentInfo?.nextPaymentDate) return null;
    const today = new Date();
    const paymentDate = new Date(paymentInfo.nextPaymentDate);
    const diffTime = paymentDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilPayment = getDaysUntilPayment();

  return (
    <div className="space-y-6">
      {/* Contract Status Banner */}
      {contractData && (
        <ContractStatusBanner
          contractStatus={contractData.contractStatus}
          kycOriginStatus={contractData.kycOriginStatus}
          kycDestinationStatus={contractData.kycDestinationStatus}
        />
      )}

      {/* KYC Status Card */}
      <KycStatusCard />

      {/* Welcome Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">
            Olá, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-surface-400">Portal do Funcionário</p>
        </div>
      </div>

      {/* Balance Card */}
      <Card className="relative overflow-hidden border-green-500/30">
        {/* Background gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-600/20 via-green-700/10 to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        
        <div className="relative p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-green-500/20">
                <DollarSign size={20} className="text-green-400" />
              </div>
              <span className="text-surface-300 font-medium">Saldo disponível</span>
            </div>
            <button
              onClick={() => setShowBalance(!showBalance)}
              className="p-2 text-surface-400 hover:text-white rounded-lg hover:bg-surface-700/50 transition-colors"
            >
              {showBalance ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          
          <div className="mb-6">
            {balanceLoading ? (
              <div className="h-10 w-48 bg-surface-700 rounded animate-pulse" />
            ) : (
              <span className="text-4xl font-bold text-white">
                {showBalance ? formatCurrency(balanceData?.availableBalance || 0) : '••••••'}
              </span>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-4 gap-3">
            <button
              onClick={() => navigate('/banking/customer/pix')}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-surface-800/80 hover:bg-surface-700 border border-surface-700 hover:border-green-500/50 transition-all"
            >
              <QrCode size={24} className="text-green-400" />
              <span className="text-xs text-surface-300">Pix</span>
            </button>
            <button
              onClick={() => navigate('/banking/customer/transfers')}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-surface-800/80 hover:bg-surface-700 border border-surface-700 hover:border-green-500/50 transition-all"
            >
              <Send size={24} className="text-green-400" />
              <span className="text-xs text-surface-300">Transferir</span>
            </button>
            <button
              onClick={() => navigate('/banking/employee/salary-advance')}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-surface-800/80 hover:bg-surface-700 border border-surface-700 hover:border-green-500/50 transition-all"
            >
              <Briefcase size={24} className="text-green-400" />
              <span className="text-xs text-surface-300">Adiantar</span>
            </button>
            <button
              onClick={() => navigate('/banking/customer/statement')}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-surface-800/80 hover:bg-surface-700 border border-surface-700 hover:border-green-500/50 transition-all"
            >
              <FileText size={24} className="text-green-400" />
              <span className="text-xs text-surface-300">Extrato</span>
            </button>
          </div>
        </div>
      </Card>

      {/* Info Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Next Payment */}
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary-500/20 flex items-center justify-center">
              <Calendar size={24} className="text-primary-400" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-surface-400">Próximo pagamento</p>
              {daysUntilPayment !== null ? (
                <>
                  <p className="text-lg font-semibold text-white">
                    {daysUntilPayment === 0 ? 'Hoje' : `${daysUntilPayment} dias`}
                  </p>
                  <p className="text-xs text-surface-500">
                    {paymentInfo?.expectedAmount && formatCurrency(paymentInfo.expectedAmount)}
                  </p>
                </>
              ) : (
                <p className="text-sm text-surface-400">-</p>
              )}
            </div>
          </div>
        </Card>

        {/* Available Advance */}
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-accent-500/20 flex items-center justify-center">
              <DollarSign size={24} className="text-accent-400" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-surface-400">Adiantamento disponível</p>
              {advanceLoading ? (
                <div className="h-6 w-24 bg-surface-700 rounded animate-pulse" />
              ) : (
                <>
                  <p className="text-lg font-semibold text-white">
                    {formatCurrency(advanceData?.availableAmount || 0)}
                  </p>
                  <p className="text-xs text-surface-500">
                    {advanceData?.percentageAvailable || 0}% do salário
                  </p>
                </>
              )}
            </div>
          </div>
        </Card>

        {/* Benefits */}
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Gift size={24} className="text-green-400" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-surface-400">Benefícios ativos</p>
              <p className="text-lg font-semibold text-white">
                {benefitsData?.activeCount || 0}
              </p>
              <p className="text-xs text-surface-500">
                {benefitsData?.totalBalance && `Saldo: ${formatCurrency(benefitsData.totalBalance)}`}
              </p>
            </div>
          </div>
        </Card>
      </div>


      {/* Main Content Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Transactions */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Últimas Transações</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/banking/customer/statement')}
              >
                Ver todas
              </Button>
            </div>

            {transactionsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-surface-700" />
                    <div className="flex-1">
                      <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                      <div className="h-3 w-24 bg-surface-700 rounded" />
                    </div>
                    <div className="h-5 w-20 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            ) : transactionsData?.entries?.length > 0 ? (
              <div className="space-y-2">
                {transactionsData.entries.slice(0, 5).map((tx: any) => (
                  <div
                    key={tx.id}
                    className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors cursor-pointer"
                  >
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${tx.type.includes('RECEIVE') || tx.type === 'DEPOSIT' || tx.type === 'SALARY'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                      }
                    `}>
                      {tx.type.includes('RECEIVE') || tx.type === 'DEPOSIT' || tx.type === 'SALARY'
                        ? <ArrowDownLeft size={20} />
                        : <ArrowUpRight size={20} />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {tx.description || tx.type}
                      </p>
                      <p className="text-xs text-surface-400">
                        {formatDate(tx.date)}
                      </p>
                    </div>
                    <span className={`
                      text-sm font-semibold
                      ${tx.type.includes('RECEIVE') || tx.type === 'DEPOSIT' || tx.type === 'SALARY'
                        ? 'text-green-400'
                        : 'text-red-400'
                      }
                    `}>
                      {tx.type.includes('RECEIVE') || tx.type === 'DEPOSIT' || tx.type === 'SALARY' ? '+' : '-'}
                      {formatCurrency(Math.abs(tx.amount))}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhuma transação recente</p>
              </div>
            )}
          </div>
        </Card>

        {/* HR Notifications & Quick Links */}
        <div className="space-y-6">
          {/* HR Notifications */}
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Bell size={20} className="text-primary-400" />
                  Notificações RH
                </h2>
              </div>

              {hrNotifications?.notifications?.length > 0 ? (
                <div className="space-y-3">
                  {hrNotifications.notifications.slice(0, 3).map((notification: any) => (
                    <div
                      key={notification.id}
                      className={`
                        p-3 rounded-lg border-l-4
                        ${notification.read
                          ? 'bg-surface-800/30 border-surface-600'
                          : 'bg-primary-500/10 border-primary-500'
                        }
                      `}
                    >
                      <p className="text-sm text-white">{notification.title}</p>
                      <p className="text-xs text-surface-400 mt-1">{notification.message}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Bell size={32} className="mx-auto text-surface-600 mb-2" />
                  <p className="text-sm text-surface-400">Sem notificações</p>
                </div>
              )}
            </div>
          </Card>

          {/* Quick Links */}
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Acesso Rápido</h2>
              
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => navigate('/banking/employee/payslip')}
                  className="flex items-center gap-3 p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors"
                >
                  <FileText size={20} className="text-primary-400" />
                  <span className="text-sm text-white">Holerite</span>
                </button>
                
                <button
                  onClick={() => navigate('/banking/employee/benefits')}
                  className="flex items-center gap-3 p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors"
                >
                  <Gift size={20} className="text-green-400" />
                  <span className="text-sm text-white">Benefícios</span>
                </button>
                
                <button
                  onClick={() => navigate('/banking/employee/hr-requests')}
                  className="flex items-center gap-3 p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors"
                >
                  <Calendar size={20} className="text-accent-400" />
                  <span className="text-sm text-white">Férias</span>
                </button>
                
                <button
                  onClick={() => navigate('/banking/employee/salary-advance')}
                  className="flex items-center gap-3 p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors"
                >
                  <TrendingUp size={20} className="text-yellow-400" />
                  <span className="text-sm text-white">Adiantamento</span>
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default EmployeeDashboard;
